
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  CreditCard,
  Users,
  BarChart3,
  Settings,
  LogOut,
  FolderTree,
  Layers,
  Heart,
} from "lucide-react";
import { useAuthStore } from "@/lib/store";
import { useNavigate } from "react-router-dom";

const menuItems = [
  { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { name: "Products", href: "/admin/products", icon: Package },
  { name: "Categories", href: "/admin/categories", icon: FolderTree },
  { name: "Collections", href: "/admin/collections", icon: Layers },
  { name: "Orders", href: "/admin/orders", icon: ShoppingCart },
  { name: "Payments", href: "/admin/payments", icon: CreditCard },
  { name: "Users", href: "/admin/users", icon: Users },
  { name: "Wishlist", href: "/admin/wishlist", icon: Heart },
  { name: "Analytics", href: "/admin/analytics", icon: BarChart3 },
  { name: "Settings", href: "/admin/settings", icon: Settings },
];

export default function AdminSidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { logout, user } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Improved active state logic
  const isActive = (href: string) => {
    if (href === "/admin") {
      // For dashboard, match /admin, /admin/, or /dashboard
      return pathname === "/admin" || pathname === "/admin/" || pathname === "/dashboard" || pathname === "/dashboard/";
    }
    // For other routes, match exact or sub-routes
    return pathname === href || pathname.startsWith(href + "/");
  };

  return (
    <div className="w-72 bg-white border-r border-gray-200 min-h-screen flex flex-col shadow-sm">
      {/* Logo */}
      <div className="p-6 border-b border-gray-100">
        <h1 className="text-2xl font-bold font-playfair bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Aarsa Admin
        </h1>
        <p className="text-sm text-gray-500 mt-1">Control Panel</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          
          return (
            <Link key={item.href} to={item.href}>
              <motion.div
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  active
                    ? "bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 shadow-sm border border-purple-100"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? "text-purple-600" : "text-gray-400"}`} />
                <span className={`font-medium text-sm ${active ? "text-purple-700" : "text-gray-700"}`}>
                  {item.name}
                </span>
                {active && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="ml-auto w-1.5 h-1.5 rounded-full bg-purple-600"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.div>
            </Link>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-100 bg-gray-50">
        <div className="mb-3 px-2">
          <p className="text-sm font-semibold text-gray-900">{user?.first_name || user?.email}</p>
          <p className="text-xs text-gray-500">Administrator</p>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all duration-200 font-medium text-sm"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
}
